import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandNav from "../components/BrandNav";
import SocialProofTicker from "../components/SocialProofTicker";
import { api } from "../lib/api";
import { BANDS } from "../lib/palette";
import { useAuth } from "../lib/auth";
import { extractError } from "../lib/format";
import { ArrowRight, BookOpen, ChevronDown, ChevronUp, CreditCard, Sparkle, X } from "lucide-react";
import { toast } from "sonner";

const READER_SAMPLE_PDF = "/samples/age_8-9_term_1_chapter_1_reader.pdf";
const READER_THUMBS = [
  { src: "/samples/thumbs/age_8-9_term_1_chapter_1_spread_a.jpg", alt: "Warm illustrated opener page" },
  { src: "/samples/thumbs/age_8-9_term_1_chapter_1_spread_b.jpg", alt: "Worked example and Wonder Moment" },
  { src: "/samples/thumbs/age_8-9_term_1_chapter_1_spread_c.jpg", alt: "Hands-on Try This At Home activity" },
];

const READER_BULLETS = [
  "A rich, illustrated narrative that expands on the audio lessons",
  "Wonder Boxes to spark curiosity throughout every chapter",
  "Hands-on \u201cTry This At Home\u201d activities with materials + adult tips",
  "Meet a real historical figure (Grace Hopper, Ada Lovelace, Alan Turing\u2026)",
  "Wonder Questions for family conversation over dinner",
  "Curiosity hooks connecting to the next chapter\u2019s theme",
];

const PRICING = [
  {
    name: "Free Preview",
    price: "£0",
    note: "Term 1 · Week 1 · Lessons 1–2 unlocked for every age band.",
    sub: "Plus one full Reader book from Chapter 1 — free to download, no sign-in needed.",
    action: "start_free",
  },
  {
    name: "Level 1",
    tier_code: "level_1_full",
    price: "£80",
    note: "One full term of a chosen age band.",
    features: [
      "20 audio lessons",
      "5 illustrated Reader books (~110 pages)",
      "Full workbook",
      "AI homework feedback",
      "Completion certificate",
    ],
    installments: [
      { tier_code: "level_1_installment_1", label: "Term 1 · £30" },
      { tier_code: "level_1_installment_2", label: "Term 2 · £40" },
      { tier_code: "level_1_installment_3", label: "Term 3 · £50" },
    ],
  },
  {
    name: "Level 2 upgrade",
    tier_code: "level_2_upgrade",
    price: "£100",
    note: "Everything in Level 1, plus:",
    features: [
      "Graded certificate — Distinction / Merit / Pass",
      "Deeper AI-scored homework analysis",
      "Priority review from the curriculum team",
    ],
  },
  {
    name: "Full Bundle",
    tier_code: "bundle",
    price: "£175",
    featured: true,
    note: "All three terms · save £45 vs. buying per-term.",
    features: [
      "60 audio lessons",
      "15 illustrated Reader books (~330 pages)",
      "3 workbooks",
      "AI feedback across all homework",
      "Level 1 + Level 2 certificates",
    ],
  },
];

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [counts, setCounts] = useState({});

  useEffect(() => {
    let alive = true;
    api
      .get("/catalog/age-bands")
      .then(({ data }) => {
        if (!alive) return;
        const m = {};
        for (const b of data.age_bands) m[b.id] = b.lesson_count;
        setCounts(m);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const goStart = () => {
    if (user && user !== false) {
      nav("/pick-band");
    } else {
      nav("/signup");
    }
  };

  return (
    <div data-testid="landing-page">
      <BrandNav />
      <main className="ftg-shell ftg-page">
        <section className="ftg-hero">
          <span className="ftg-hero__eyebrow" data-testid="hero-eyebrow">
            Qualification Evaluation UK · New for 2026
          </span>
          <h1 className="ftg-hero__title" data-testid="hero-title">
            {"Algorithmic thinking through "}<em>STEAM</em>{" — for ages 6 to 17."}
          </h1>
          <p className="ftg-hero__sub" data-testid="hero-sub">
            300 spoken lessons across five age bands. Written by curriculum
            specialists, voiced clearly, done at home in twenty minutes a
            session. Culturally neutral. Maths at the core.
          </p>
          <div className="ftg-hero__ctas">
            <button
              onClick={goStart}
              className="ftg-btn ftg-btn--primary ftg-btn--large"
              data-testid="hero-start-btn"
            >
              Start Free Preview <ArrowRight size={18} />
            </button>
            <a href="#pricing" className="ftg-btn ftg-btn--ghost ftg-btn--large" data-testid="hero-pricing-link">
              {"See what's included"}
            </a>
          </div>
        </section>

        <section className="ftg-bands" data-testid="age-bands-section">
          <h2 className="ftg-section-title">Choose your child&apos;s age band</h2>
          <p className="ftg-section-sub">
            Each band runs three terms of 20 lessons — a full year, 60 lessons.
            Lessons are audio-first, homework alternates between worksheets and
            maker projects.
          </p>
          <div className="ftg-bands__grid">
            {BANDS.map(({ id, label, tagline, gradient, Icon, accent }) => (
              <article
                key={id}
                className="ftg-band-card"
                style={{
                  background: `linear-gradient(140deg, ${gradient[0]} 0%, ${gradient[1]} 100%)`,
                }}
                data-testid={`band-card-${id}`}
              >
                <div>
                  <span className="ftg-band-card__icon" style={{ color: accent }}>
                    <Icon size={22} strokeWidth={2} />
                  </span>
                  <h3 className="ftg-band-card__label">{label}</h3>
                  <p className="ftg-band-card__tag">{tagline}</p>
                </div>
                <div className="ftg-band-card__count" data-testid={`band-count-${id}`}>
                  {counts[id] ?? 60} lessons · 3 terms
                </div>
              </article>
            ))}
          </div>
        </section>

        <section id="pricing" className="ftg-pricing" data-testid="pricing-section">
          <h2 className="ftg-section-title">Simple pricing</h2>
          <p className="ftg-section-sub">
            One-time GBP payments. Test-mode Stripe checkout is live.
          </p>
          <div className="ftg-pricing__grid">
            {PRICING.map((p) => (
              <div
                key={p.name}
                className={`ftg-price-card${p.featured ? " ftg-price-card--featured" : ""}`}
                data-testid={`pricing-card-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="ftg-price-card__name">{p.name}</div>
                <div className="ftg-price-card__price">{p.price}</div>
                <div className="ftg-price-card__note">{p.note}</div>
                {p.sub && (
                  <div className="ftg-price-card__sub" data-testid={`pricing-sub-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {p.sub}
                  </div>
                )}
                {p.features && (
                  <ul className="ftg-price-card__features" data-testid={`pricing-features-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {p.features.map((f) => (
                      <li key={f} className="ftg-price-card__feature">{f}</li>
                    ))}
                  </ul>
                )}
                {p.action === "start_free" && (
                  <button
                    type="button"
                    onClick={goStart}
                    className="ftg-btn ftg-btn--ghost ftg-btn--sm"
                    data-testid="pricing-start-free"
                  >
                    Start free preview <ArrowRight size={14} />
                  </button>
                )}
                {p.tier_code && (
                  <BuyButton tier_code={p.tier_code} label="Buy now" />
                )}
                {p.installments && (
                  <div className="ftg-price-card__installments" data-testid="pricing-installments">
                    <div className="ftg-price-card__installments-hd">Or pay term-by-term</div>
                    {p.installments.map((it) => (
                      <BuyButton key={it.tier_code} tier_code={it.tier_code} label={it.label} small />
                    ))}
                  </div>
                )}
                {(p.tier_code || p.action === "start_free") && (
                  <WhatsInsideReveal cardName={p.name} />
                )}
              </div>
            ))}
          </div>

          <FoundersCohort />
        </section>

        <LaunchWaitlist />

        <footer className="ftg-foot" data-testid="landing-footer">
          © {new Date().getFullYear()} FutureTec Global · A programme of Qualification Evaluation UK Ltd.
        </footer>
      </main>
    </div>
  );
}

function BuyButton({ tier_code, label = "Buy now", small = false }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);

  const click = async () => {
    if (!user) {
      // Save intent + bounce to signup, then back to /#pricing
      try {
        sessionStorage.setItem("ftg_pending_tier", tier_code);
      } catch { /* no-op */ }
      nav("/signup");
      return;
    }
    setBusy(true);
    try {
      // Users may not have an enrollment yet — the checkout endpoint requires age_band.
      const { data: mine } = await api.get("/enrollments/me").catch(() => ({ data: { enrollments: [] } }));
      const age_band = mine.enrollments?.[0]?.age_band;
      if (!age_band) {
        nav(`/pick-band?next=checkout&tier=${encodeURIComponent(tier_code)}`);
        setBusy(false);
        return;
      }
      const { data } = await api.post("/checkout/session", {
        tier_code,
        age_band,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        toast.error(e.response.data?.detail || "Founder's cohort is full.");
      } else {
        toast.error(extractError(e, "Could not start checkout. Please try again."));
      }
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={click}
      disabled={busy}
      className={`ftg-btn ${small ? "ftg-btn--ghost ftg-btn--sm" : "ftg-btn--secondary"}`}
      data-testid={`buy-${tier_code}`}
    >
      {busy ? "Opening…" : (
        <>
          {!small && <CreditCard size={14} />} {label}
        </>
      )}
    </button>
  );
}

function WhatsInsideReveal({ cardName }) {
  const [open, setOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { src, alt } | null
  const testSlug = (cardName || "card").toLowerCase().replace(/[^a-z0-9]+/g, "-");

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      api.post("/marketing/event", {
        event_type: "pricing_reveal_expanded",
        referrer: cardName,
      }).catch(() => { /* fire and forget */ });
    }
  };

  const onSample = () => {
    api.post("/marketing/event", {
      event_type: "reader_sample_downloaded",
      referrer: cardName,
    }).catch(() => { /* fire and forget */ });
    // Open sample PDF in a new tab — server-side no auth required for the
    // /samples/* static assets, so this works for anonymous browsers.
    window.open(READER_SAMPLE_PDF, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="ftg-inside" data-testid={`inside-${testSlug}`}>
      <button
        type="button"
        onClick={toggle}
        className="ftg-inside__toggle"
        aria-expanded={open}
        data-testid={`inside-toggle-${testSlug}`}
      >
        See what&apos;s inside {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {open && (
        <div className="ftg-inside__body" data-testid={`inside-body-${testSlug}`}>
          <div className="ftg-inside__thumbs" data-testid={`inside-thumbs-${testSlug}`}>
            {READER_THUMBS.map((t) => (
              <button
                key={t.src}
                type="button"
                className="ftg-inside__thumb"
                onClick={() => setLightbox(t)}
                aria-label={`Preview ${t.alt}`}
                data-testid={`inside-thumb-${testSlug}-${t.src.split("_").pop().replace(".jpg","")}`}
              >
                <img src={t.src} alt={t.alt} loading="lazy" />
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={onSample}
            className="ftg-btn ftg-btn--ghost ftg-btn--sm ftg-inside__sample-btn"
            data-testid={`inside-sample-${testSlug}`}
          >
            <BookOpen size={14} /> Preview Chapter 1 free
          </button>

          <ul className="ftg-inside__bullets">
            {READER_BULLETS.map((b) => (
              <li key={b} className="ftg-inside__bullet">
                <span className="ftg-inside__bullet__dot" aria-hidden="true" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {lightbox && (
        <div
          className="ftg-inside__lightbox"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          data-testid={`inside-lightbox-${testSlug}`}
        >
          <img src={lightbox.src} alt={lightbox.alt} />
          <button
            type="button"
            className="ftg-inside__lightbox__close"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            aria-label="Close preview"
          >
            <X size={20} />
          </button>
        </div>
      )}
    </div>
  );
}

function FoundersCohort() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [seats, setSeats] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    let alive = true;
    api.get("/enrollments/founder-count")
      .then(({ data }) => { if (alive) setSeats(data); })
      .catch(() => { /* silent */ });
    return () => { alive = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/waitlist", {
        email,
        source: "founders_cohort",
        preferred_tier: "founder",
      });
      setDone(true);
      toast.success(data.message || "You're on the list.");
    } catch (err) {
      toast.error(extractError(err, "Could not join list."));
    } finally {
      setBusy(false);
    }
  };

  const soldOut = seats && seats.remaining <= 0;
  const seatsCopy = seats
    ? (soldOut
        ? "All 30 founder seats claimed — join the launch waitlist below."
        : `${seats.claimed} of ${seats.total} seats claimed · ${seats.remaining} remaining`)
    : "First 30 students only";

  return (
    <div className="ftg-founders" data-testid="founders-cohort">
      <div className="ftg-founders__badge">
        <Sparkle size={14} /> {soldOut ? "Sold out" : "Limited to 30 seats"}
      </div>
      <h3 className="ftg-founders__title">Founder&apos;s Cohort · £50</h3>
      <p className="ftg-founders__note">
        Level 1 at 37% off (£50 instead of £80). Direct feedback from the curriculum author on the first term,
        and a Founder&apos;s badge on your child&apos;s certificate.
      </p>
      <div className="ftg-founders__seats" data-testid="founders-seats">{seatsCopy}</div>
      <div className="ftg-founders__proof-row">
        <SocialProofTicker />
      </div>

      {!soldOut && (
        <div className="ftg-founders__actions">
          <BuyButton tier_code="founder" label={user ? "Claim my founder seat · £50" : "Sign up & claim (£50)"} />
        </div>
      )}

      {done ? (
        <div className="ftg-founders__done" data-testid="founders-done">
          You&apos;re on the founder&apos;s list. Thank you — watch for launch details.
        </div>
      ) : (
        <form className="ftg-founders__form" onSubmit={submit} data-testid="founders-form">
          <input
            type="email"
            required
            placeholder={soldOut ? "your email for the next cohort" : "or join the list first"}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="ftg-field__input"
            data-testid="founders-email-input"
          />
          <button
            type="submit"
            disabled={busy}
            className="ftg-btn ftg-btn--ghost"
            data-testid="founders-submit-btn"
          >
            {busy ? "Joining…" : (soldOut ? "Join launch list" : "Or just join the list")}
          </button>
        </form>
      )}
    </div>
  );
}

function LaunchWaitlist() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/waitlist", {
        email,
        source: "general_launch",
        preferred_tier: "any",
      });
      setDone(true);
      toast.success(data.message || "You're on the list.");
    } catch (err) {
      toast.error(extractError(err, "Could not join list."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ftg-launch" data-testid="launch-waitlist">
      <div className="ftg-launch__inner">
        <div>
          <h3 className="ftg-launch__title">Notify me when the full course launches</h3>
          <p className="ftg-launch__sub">
            One email. When your child&apos;s age band opens for enrolment, you&apos;ll be the first to know.
          </p>
        </div>
        {done ? (
          <div className="ftg-launch__done" data-testid="launch-done">
            You&apos;re on the launch list — thank you.
          </div>
        ) : (
          <form className="ftg-launch__form" onSubmit={submit} data-testid="launch-form">
            <input
              type="email"
              required
              placeholder="your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="ftg-field__input"
              data-testid="launch-email-input"
            />
            <button
              type="submit"
              disabled={busy}
              className="ftg-btn ftg-btn--primary"
              data-testid="launch-submit-btn"
            >
              {busy ? "Adding…" : "Notify me"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
